import { useEffect, useState, createContext, useContext, useCallback, useRef, type ReactNode } from "react";

// ─── Types ────────────────────────────────────────────────────────────────────
type ToastType = "error" | "success" | "info";

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastCtx {
  show: (message: string, type?: ToastType) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────
const ToastContext = createContext<ToastCtx | null>(null);

// ─── Imperative bridge ─────────────────────────────────────────────────────────
// Lets code outside the React tree (e.g. the admin-ui `message.*` shim) push a
// toast without needing the hook. The provider below is the only subscriber.
let imperativeShow: ((message: string, type?: ToastType) => void) | null = null;

export function pushToast(message: string, type: ToastType = "error"): void {
  if (imperativeShow) imperativeShow(message, type);
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const counter = useRef(0);

  const show = useCallback((message: string, type: ToastType = "error") => {
    const id = ++counter.current;
    setItems((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 3500);
  }, []);

  useEffect(() => {
    imperativeShow = show;
    return () => {
      if (imperativeShow === show) imperativeShow = null;
    };
  }, [show]);

  return (
    <ToastContext.Provider value={{ show }}>
      {children}
      <div
        style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 8,
          pointerEvents: "none",
        }}
      >
        {items.map((item) => (
          <ToastBubble key={item.id} item={item} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// ─── Bubble ───────────────────────────────────────────────────────────────────
function ToastBubble({ item }: { item: ToastItem }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const bg =
    item.type === "error" ? "#D93025" :
    item.type === "success" ? "#00731E" :
    "#1A73E8";

  return (
    <div
      className="admin-overlay"
      style={{
        background: bg,
        color: "#fff",
        padding: "14px 20px",
        borderRadius: 12,
        fontSize: 14,
        fontWeight: 600,
        lineHeight: "20px",
        maxWidth: "calc(100vw - 32px)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
        pointerEvents: "auto",
        transform: visible ? "scale(1)" : "scale(0.85)",
        opacity: visible ? 1 : 0,
        transition: "transform 0.25s ease, opacity 0.25s ease",
        wordBreak: "break-word",
        textAlign: "center",
      }}
    >
      {item.message}
    </div>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useToast(): ToastCtx {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
